import { useState } from 'react';
import { SparklesIcon, MapIcon } from '@heroicons/react/24/outline';
import { CTA, INSET_PANEL, METRIC_LABEL } from '@components/ui/designTokens';
import type { SupportHelp, SupportStep } from '../hooks/useSupportHelp';
import { GuidedTour } from './GuidedTour';
import { Markdown } from './Markdown';

/**
 * Sección "Asistente IA" para los modales de error: muestra la explicación, los
 * pasos sugeridos y un botón "Guíame en pantalla" que ejecuta esos pasos en el
 * portal real del cliente (navega + resalta). No renderiza nada si no aplica.
 *
 * Vestida con el bloque destacado del sistema (INSET_PANEL) y los rótulos mono
 * de métrica, para que se lea como una sección más del modal y no como un
 * recuadro ajeno.
 *
 * Si recibe `onGuide`, delega el arranque del tour al contenedor (p. ej. para que
 * cierre su modal primero); si no, lo ejecuta internamente.
 */
export function SupportAssistantPanel({
  help,
  onGuide,
  guideEnabled = true,
}: {
  help: SupportHelp;
  onGuide?: (steps: SupportStep[]) => void;
  guideEnabled?: boolean;
}) {
  const [touring, setTouring] = useState(false);
  if (!help.loading && !help.reply && !help.failed) return null;

  const hasSteps = !!help.steps && help.steps.length > 0;
  // El botón "Guíame en pantalla" (guía interactiva) solo si la feature está activa.
  const canGuide = hasSteps && guideEnabled;

  function startGuide() {
    if (onGuide) onGuide(help.steps);
    else setTouring(true);
  }

  return (
    <div className={`mt-4 p-4 ${INSET_PANEL}`}>
      <div className="mb-2.5 flex items-center gap-2">
        <span
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-[#6b239e] text-white"
          aria-hidden="true"
        >
          <SparklesIcon className="h-4 w-4" />
        </span>
        <h4 className="text-[14px] font-extrabold tracking-[-0.01em] text-ink-850 dark:text-white">Asistente IA</h4>
      </div>

      {help.loading && (
        <div className="flex items-center gap-2 text-[13px] font-medium text-secondary-500">
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
          Analizando el error…
        </div>
      )}

      {help.failed && (
        <p className="text-[13px] text-secondary-500">
          No se pudo obtener la ayuda del asistente en este momento.
        </p>
      )}

      {/* 1) POR QUÉ pasó + qué hacer (explicación del agente). */}
      {help.reply && (
        <div>
          <p className={`mb-1 ${METRIC_LABEL}`}>POR QUÉ PASÓ</p>
          <Markdown text={help.reply} className="text-[13.5px] leading-[1.55] text-secondary-600 dark:text-secondary-300" />
        </div>
      )}

      {/* 2) PASOS TEXTUALES (numerados) para resolverlo a mano. */}
      {hasSteps && (
        <div className="mt-3">
          <p className={`mb-1 ${METRIC_LABEL}`}>PASOS PARA RESOLVERLO</p>
          <ol className="list-decimal space-y-1 pl-5 text-[13.5px] leading-[1.5] text-secondary-600 marker:font-semibold marker:text-teal-600 dark:text-secondary-300">
            {help.steps
              .filter((s) => s.label)
              .map((s, i) => (
                <li key={i}>
                  <Markdown text={s.label as string} className="inline" />
                </li>
              ))}
          </ol>
        </div>
      )}

      {/* 3) Opción APARTE: que el asistente te lleve de la mano en pantalla. */}
      {canGuide && (
        <div className="mt-4 flex flex-col gap-2 border-t border-dashed border-[#e2e8f0] pt-3.5 dark:border-secondary-700 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-[13px] text-secondary-500 dark:text-secondary-400">
            ¿Prefieres que te lleve de la mano?
          </span>
          <button type="button" onClick={startGuide} className={`${CTA} py-2 text-[13px]`}>
            <MapIcon className="h-4 w-4" /> Guíame en pantalla
          </button>
        </div>
      )}

      {/* El feedback "¿te funcionó?" es un toast en el borde inferior (persiste tras
          cerrar el modal) — lo dispara SupportErrorProvider. */}

      {/* Tour interno (solo si NO hay onGuide; si lo hay, lo maneja el contenedor). */}
      {!onGuide && touring && hasSteps && (
        <GuidedTour steps={help.steps} onClose={() => setTouring(false)} />
      )}
    </div>
  );
}
